import { useCallback, useRef, useState } from 'react';
// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  OutlinedInput,
  Typography
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';

// project imports
import useScriptRef from 'hooks/useScriptRef';
import AnimateButton from 'ui-component/extended/AnimateButton';

// assets
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { logIn, resetPassword } from 'firebaseService/firebaseAuth';
import SnackbarAlert from 'ui-component/Alerts/SnackbarAlerts';
import LoaderSpinner from 'ui-component/LoaderSpinner';
import { validateRecaptchaToken } from '../services/ReCaptchaService';

// ============================|| FIREBASE - LOGIN ||============================ //

const FirebaseLogin = ({ ...others }) => {
  const theme = useTheme();
  const formikRef = useRef(null);
  const scriptedRef = useScriptRef();
  const [loader, setLoader] = useState(false);
  const [isRecaptchaVerified, setRecaptchaVerified] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const onVerify = useCallback(async (token) => {
    const result = await validateRecaptchaToken(token);
    setRecaptchaVerified(result.success);
  });

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const [snackbarData, setSnackbarData] = useState({
    open: false,
    severity: 'success',
    message: '',
    duration: 3000
  });

  const closeSnackbar = () => {
    setSnackbarData({
      ...snackbarData,
      open: false
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const refreshCaptcha = () => setRefreshReCaptcha((r) => !r);

  const handleForgotPassword = async () => {
    if (!formikRef.current) {
      return;
    }

    const { values, errors } = formikRef.current;
    const email = values.email;

    if (!email) {
      formikRef.current.setFieldError('email', 'Por favor, ingresa tu correo electrónico.');
      return;
    }

    if (errors.email) {
      formikRef.current.setFieldError('email', 'Por favor, ingresa un correo electrónico válido.');
      return;
    }
    try {
      setLoader(true);
      const result = await resetPassword(email);
      if (result.success) {
        setSnackbarData({
          open: true,
          severity: 'success',
          message: 'Se ha enviado un correo electrónico para restablecer tu contraseña. Por favor, verifica tu bandeja de entrada.'
        });
      } else {
        setSnackbarData({
          open: true,
          severity: 'error',
          message: 'Error al intentar restablecer la contraseña. Por favor, inténtalo de nuevo.'
        });
      }
    } catch (error) {
      console.error('Error al intentar restablecer la contraseña:', error);
      setSnackbarData({
        open: true,
        severity: 'error',
        message: 'Error inesperado al intentar restablecer la contraseña. Por favor, inténtalo de nuevo.'
      });
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <Grid container direction="column" justifyContent="center" spacing={2}>
        <Grid item xs={12} container alignItems="center" justifyContent="center">
          <Typography variant="caption" fontSize="18px" textAlign={'center'} marginBottom={3}>
            Portal de registros de proyectos para el Gobierno de El Salvador
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">Iniciar sesión con dirección de correo electrónico</Typography>
          </Box>
        </Grid>
      </Grid>

      <Formik
        innerRef={formikRef}
        initialValues={{
          email: '',
          password: '',
          submit: null
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string().email('El email debe ser valido').max(255).required('Email es requerido'),
          password: Yup.string().max(255).required('La contraseña es requerida')
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            if (!scriptedRef.current) {
              return;
            }

            if (!isRecaptchaVerified) {
              await setSnackbarData({
                open: true,
                severity: 'error',
                message: '¡Validación de seguridad no completada!'
              });
              return;
            }

            refreshCaptcha();

            const result = await logIn(values.email, values.password);
            const successMessage = 'Inicio de sesión exitoso. Usuario:';
            const errorMessage = 'Error de inicio de sesión:';

            if (result.error) {
              setStatus({ success: false });
              if (result.error.message.includes('Firebase: Error (auth/user-disabled)')) {
                setErrors({ submit: 'Esta cuenta aun no ha sido autorizada, por favor contacte al administrador del sistema.' });
              } else if (result.error.message.includes('Firebase: Error (auth/invalid-login-credentials)')) {
                setErrors({ submit: 'Las credenciles incorrectas.' });
              } else {
                setStatus({ success: true });
              }
            }

            setSnackbarData({
              open: true,
              severity: result.error ? 'error' : 'success',
              message: result.error ? `${errorMessage} ${result.error.message}` : `${successMessage} ${result.user.email}`
            });
          } catch (error) {
            console.error(error);
            setStatus({ success: false });
            setErrors({ submit: error.message });

            setSnackbarData({
              open: true,
              severity: 'error',
              message: 'Error inesperado'
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <FormControl fullWidth error={Boolean(touched.email && errors.email)} sx={{ ...theme.typography.customInput }}>
              <InputLabel htmlFor="outlined-adornment-email-login">Email Address / Username</InputLabel>
              <OutlinedInput
                id="outlined-adornment-email-login"
                type="email"
                value={values.email}
                name="email"
                onBlur={handleBlur}
                onChange={handleChange}
                label="Email Address / Username"
                inputProps={{}}
              />
              {touched.email && errors.email && (
                <FormHelperText error id="standard-weight-helper-text-email-login">
                  {errors.email}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth error={Boolean(touched.password && errors.password)} sx={{ ...theme.typography.customInput }}>
              <InputLabel htmlFor="outlined-adornment-password-login">Password</InputLabel>
              <OutlinedInput
                id="outlined-adornment-password-login"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
                inputProps={{}}
              />
              {touched.password && errors.password && (
                <FormHelperText error id="standard-weight-helper-text-password-login">
                  {errors.password}
                </FormHelperText>
              )}
            </FormControl>

            <Box direction="row" alignItems="center" justifyContent="center" marginBottom={2}>
              <Typography
                variant="subtitle1"
                color="secondary"
                sx={{ textDecoration: 'none', cursor: 'pointer' }}
                onClick={handleForgotPassword}
              >
                ¿Has olvidado tu contraseña?
              </Typography>
            </Box>
            {errors.submit && (
              <Box sx={{ mt: 2 }}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}

            {isRecaptchaVerified ? (
              <Box sx={{ mt: 2 }}>
                <AnimateButton>
                  <Button
                    disableElevation
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="secondary"
                  >
                    Ingresar
                  </Button>
                </AnimateButton>
              </Box>
            ) : (
              <LinearProgress />
            )}
          </form>
        )}
      </Formik>
      <LoaderSpinner isOpen={loader} setIsOpen={setLoader} />
      <SnackbarAlert {...snackbarData} closeSnackbar={closeSnackbar} />
      <GoogleReCaptcha refreshReCaptcha={refreshReCaptcha} onVerify={onVerify} />
    </>
  );
};

export default FirebaseLogin;
